import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, X, AlertTriangle, Info, Database, FileText } from 'lucide-react';
import insightsService from '../../services/insightsService';

// =============================================================================
// "What the AI can see" and "the briefing it is sent".
//
// Lifted out of Insights.jsx unchanged when that page became a conversation —
// these two were already doing their job and the thread had no business growing
// another 300 lines around them.
//
// Together they are the honesty surface of the whole feature: one shows what the
// model is allowed to look at for YOUR role, the other shows the exact words it
// is told. Between them they answer "why did it say that" and "what did you tell
// it about us" without anybody needing the server log.
// =============================================================================

export const ModalShell = ({ title, icon: Icon, onClose, children, wide = false }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6 font-sans" onMouseDown={onClose}>
    <div
      className={`bg-white dark:bg-slate-800 w-full ${wide ? 'max-w-4xl' : 'max-w-2xl'} rounded-xl shadow-2xl flex flex-col max-h-[88vh] overflow-hidden`}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500" />
          <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
        </div>
        <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">{children}</div>
    </div>
  </div>
);

const TONES = {
  info: 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300',
  warn: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300',
  danger: 'border-red-300 bg-red-50 text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300',
};

export const Notice = ({ tone, icon: Icon, title, children }) => (
  <div className={`mb-4 flex items-start gap-2 px-3 py-2.5 rounded-lg border ${TONES[tone]}`}>
    <Icon className="w-4 h-4 shrink-0 mt-0.5" />
    <div className="min-w-0">
      <p className="m-0 text-xs font-bold">{title}</p>
      {children && <div className="m-0 mt-1 text-[11px] opacity-90">{children}</div>}
    </div>
  </div>
);

// -----------------------------------------------------------------------------
// The briefing, exactly as the model receives it on the planning turn.
// -----------------------------------------------------------------------------
export const BriefingModal = ({ modelCode, onClose }) => {
  const [state, setState] = useState({ loading: true, data: null, error: '' });

  useEffect(() => {
    (async () => {
      try {
        const res = await insightsService.getBriefing(modelCode);
        setState({ loading: false, data: res.data, error: '' });
      } catch (e) {
        setState({ loading: false, data: null, error: e.response?.data?.message || e.message });
      }
    })();
  }, [modelCode]);

  return (
    <ModalShell title="The briefing sent to the model" icon={FileText} onClose={onClose} wide>
      {state.loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : state.error ? (
        <Notice tone="danger" icon={AlertTriangle} title="Could not build the briefing.">{state.error}</Notice>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap gap-4 text-[11px] text-slate-500 dark:text-slate-400">
            <span><strong>{state.data.objects_included}</strong> objects</span>
            <span>about <strong>{state.data.estimated_tokens.toLocaleString()}</strong> tokens</span>
            <span><strong>{state.data.characters.toLocaleString()}</strong> characters</span>
            {state.data.model && <span>trimmed for <strong>{state.data.model.display_name}</strong></span>}
          </div>
          <Notice tone="info" icon={Info} title="This describes your data. It does not contain any of it.">
            Object names, what a row means, units and how figures relate — no budget figures, customer names or prices.
            This goes out on every question; result figures only go out if your administrator enabled that for the provider.
          </Notice>
          {state.data.objects_omitted?.length > 0 && (
            <Notice tone="info" icon={Info} title={`${state.data.objects_omitted.length} object(s) omitted to fit this model's context window.`}>
              {state.data.objects_omitted.join(', ')} — a model with a larger window would be given these too.
            </Notice>
          )}
          <pre className="m-0 p-4 rounded-lg bg-slate-900 text-slate-100 text-[11px] leading-relaxed overflow-x-auto whitespace-pre-wrap">
            {state.data.briefing}
          </pre>
        </>
      )}
    </ModalShell>
  );
};

// -----------------------------------------------------------------------------
// The catalogue, grouped by subject area. `pitfalls` is shown because it is the
// most useful thing here for a person too: a list of the ways this company's data
// is commonly misread.
// -----------------------------------------------------------------------------
export const CatalogueModal = ({ data, onClose }) => {
  const byDomain = useMemo(() => {
    const map = new Map();
    for (const d of data.domains) map.set(d.domain_code, { domain: d, objects: [] });
    const loose = [];
    for (const o of data.objects) {
      const bucket = map.get(o.domain_code);
      if (bucket) bucket.objects.push(o); else loose.push(o);
    }
    const out = [...map.values()].filter((x) => x.objects.length > 0);
    if (loose.length) out.push({ domain: { domain_code: 'OTHER', domain_name: 'Uncategorised' }, objects: loose });
    return out;
  }, [data]);

  return (
    <ModalShell title="What the AI can see" icon={Database} onClose={onClose} wide>
      <div className="mb-4 flex flex-wrap gap-4 text-[11px] text-slate-500 dark:text-slate-400">
        <span><strong>{data.summary.objects}</strong> objects</span>
        <span><strong>{data.summary.domains}</strong> subject areas</span>
        <span><strong>{data.summary.columns}</strong> annotated columns</span>
        <span><strong>{data.summary.joins}</strong> documented joins</span>
      </div>

      <Notice tone="info" icon={Info} title="This list is scoped to your current role.">
        An object is here only if your acting role holds the module that owns it, and a query can only ever use what is
        on this list. Salary data needs the People module explicitly — an administrator without it does not see it either.
      </Notice>

      {byDomain.map(({ domain, objects }) => (
        <div key={domain.domain_code} className="mb-5">
          <h3 className="m-0 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {domain.domain_name}
          </h3>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700 overflow-hidden">
            {objects.map((o) => (
              <div key={o.object_name} className="px-4 py-3 bg-white dark:bg-slate-800">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <code className="text-xs font-semibold text-slate-900 dark:text-slate-100">{o.object_name}</code>
                  {o.object_kind === 'view' && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-500">view</span>
                  )}
                  <span className="text-xs text-slate-500 dark:text-slate-400">{o.display_name}</span>
                </div>
                <p className="m-0 mt-1 text-[11px] text-slate-500 dark:text-slate-400">{o.description}</p>
                <p className="m-0 mt-1 text-[11px] text-slate-400"><strong>Grain:</strong> {o.grain}</p>
                {o.pitfalls && (
                  <p className="m-0 mt-1 text-[11px] text-amber-700 dark:text-amber-400">
                    <strong>Watch out:</strong> {o.pitfalls}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <p className="m-0 mt-2 text-[11px] text-slate-400">
        The catalogue lives in the <code>ai_catalogue_*</code> tables. Adding an object there changes what the AI knows
        with no deploy.
      </p>
    </ModalShell>
  );
};
